import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import usePriorityApi from "../../http/priority/api";
import { Priority, UpsertPriority } from "../../http/priority/types";
import { PaginationQuery } from "../../types";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import TH from "../ui/TH";
import PriorityForm, { PriorityFormBody } from "./PriorityForm";

const Priorities: FC = () => {
  const { usePriorities: getPriorities } = usePriorityApi();

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { isOpen, open, close } = useToggle();
  const [editPriority, setEditPriority] = useState<Priority>();
  const [deletePriority, setDeletePriority] = useState<Priority>();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const priorityApi = usePriorityApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  const pageContext = usePageContext();

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };

  const { data, error } = getPriorities(query);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.common.priorities });
  }, [i18n.language]);

  const onSubmit = async (formData: PriorityFormBody) => {
    const body: UpsertPriority = {
      name: formData.name,
      days: formData.days,
    };
    if (!editPriority) {
      try {
        await priorityApi.createPriority(body);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    } else {
      try {
        await priorityApi.updatePriority(body, editPriority.id);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    }
  };

  const actions: Action<Priority>[] = [
    {
      title: i18n.t.common.edit,
      onClick: (item) => {
        setEditPriority(item);
        open();
      },
      icon: SvgEdit,
    },
    {
      title: i18n.t.common.delete,
      onClick: setDeletePriority,
      icon: SvgDelete,
      color: "red",
      isDivider: true,
    },
  ];

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        onClose={close}
        title={!editPriority ? i18n.t.priorities.newPriority : i18n.t.priorities.editPriority}
      >
        <PriorityForm priority={editPriority} onSubmit={onSubmit} key={isOpen ? editPriority?.id ?? "new" : "closed"} />
      </Sidebar>

      <Table<Priority>
        buttons={{
          main: (
            <Button
              buttonProps={{
                onClick: () => {
                  setEditPriority(undefined);
                  open();
                },
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        rowActions={actions}
        onRowClick={(item) => {
          setEditPriority(item);
          open();
        }}
        filters={filters}
        head={
          <tr>
            <TH order={orderOptions} fieldName="priority.name" title={i18n.t.common.name} />
            <TH order={orderOptions} fieldName="priority.days" title={i18n.t.common.days} />
            <th align="left"></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.name}</td>
            <td>{item.days}</td>
          </>
        )}
        items={data?.items}
        total={data?.total}
        error={error}
      />
      {!!deletePriority && (
        <ModalDialog
          onClose={() => setDeletePriority(undefined)}
          title={i18n.t.priorities.delete}
          isSubmitting={isModalSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setDeletePriority(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                setIsModalSubmitting(true);
                if (!deletePriority) return;
                try {
                  await priorityApi.deletePriority(deletePriority.id);
                  setIsModalSubmitting(false);
                  setDeletePriority(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.priorities.questions.delete}
        </ModalDialog>
      )}
    </>
  );
};

export default Priorities;
